import { Navigate, Outlet, useLocation, useParams } from "react-router";
import type { UserRole } from "@gatiq/shared";
import { useAuth } from "./AuthProvider";
import { Spinner } from "@/ui";

/**
 * Route guard. Three checks, in order:
 *
 *   1. signed in at all
 *   2. forced password change not outstanding (D-04)
 *   3. role is allowed here
 *
 * This is convenience, not security — every API route enforces the same rules
 * server-side. Hiding a button never protects an endpoint.
 */
export function RequireAuth({ roles }: { roles: readonly UserRole[] }) {
  const { user, loading } = useAuth();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner className="size-6 text-brand" />
      </div>
    );
  }

  if (!user) {
    /*
     * Karigars have their own sign-in — a mobile number and a one-time code,
     * no password — so sending them to the staff form would offer credentials
     * they have never had. The role is unknown here (that is the point), so
     * the area of the app they were in stands in for it.
     */
    const karigarArea = orgSlug ? location.pathname.startsWith(`/${orgSlug}/karigar`) : false;
    const loginPath = orgSlug
      ? `/${orgSlug}/login${karigarArea ? "/karigar" : ""}`
      : "/login";
    return <Navigate replace state={{ from: location.pathname }} to={loginPath} />;
  }

  if (user.mustChangePassword) {
    const target = orgSlug ? `/${orgSlug}/set-password` : "/set-password";
    if (location.pathname !== target) return <Navigate replace to={target} />;
  }

  if (!roles.includes(user.role)) {
    return <Navigate replace to={homeFor(user)} />;
  }

  return <Outlet />;
}

/** Where a user belongs after signing in, or after hitting a forbidden area. */
export function homeFor(user: { role: UserRole; orgSlug: string | null }): string {
  const slug = user.orgSlug ?? "";
  switch (user.role) {
    case "super_admin":
      return "/super-admin/organizations";
    case "org_admin":
      return `/${slug}/admin`;
    case "sales_rep":
    case "distributor":
    case "sub_distributor":
      return `/${slug}/sales`;
    case "counter":
      return `/${slug}/counter`;
    case "karigar":
      return `/${slug}/karigar`;
    default:
      return "/";
  }
}

/**
 * The bare portal address (`/oliva`) — what every WhatsApp message links to.
 * Signed in to this org: your home. Otherwise: the sign-in page, which also
 * points karigars to their own sign-in.
 */
export function OrgRoot() {
  const { user, loading } = useAuth();
  const { orgSlug } = useParams();
  if (loading) return null;
  if (user && user.orgSlug === orgSlug) return <Navigate replace to={homeFor(user)} />;
  return <Navigate replace to={`/${orgSlug ?? ""}/login`} />;
}
